import React from "react";
import { useState, useEffect } from "react";
import Button from "../ui-components/Button";
import Input from "../ui-components/Input";
import Select from "../ui-components/Select";
import sortBlock from "../utils/sortBlock";
import SelectProduct from "./SelectProduct";
import ServiceConfiguratorCompositeQuestionFieldSelector
  from "@components/ServiceConfiguratorCompositeQuestionFieldSelector";
import {BlockSelector} from "@components/BlockSelector";
import ServiceConfiguratorCompositeOutputPathSelector from "./ServiceConfiguratorCompositeOutputPathSelector";
import SearchSelect from "../ui-components/SearchSelect";

const outputTypes = [
  { value: "product_question", name: "Question with a product as answer" },
  { value: "recursive_question", name: "Recursive question total" },
  { value: "product", name: "Fixed product" },
  { value: "number_question", name: "Question with a number as answer" },
  { value: "output", name: "Value from another output" },
  { value: "number", name: "Number" },
];

const productColumns = [
  {
    value: "prezzo",
    name: "Price",
  },
  {
    value: "resa",
    name: "Resa",
  },
  {
    value: "spessore_min",
    name: "Spessore min",
  },
];

const ServiceConfiguratorBlockCalculationExpressionGenerator = ({
  expression,
  questions,
  updateExpression,
  setSelectedOperator,
  outputs,
  recursiveQuestions,
  data,
  lockMissingSelectedOptions=false,
  editable=true,
  service,
  block,
}) => {
  const expressionObject =
    expression === "expression" ? {} : JSON.parse(expression);
  const isFromDifferentService = block.service_id !== service.id;

  const servicesOptions = data.filter(d => d.type === 'service').map(
    d => ({
      value: d.id,
      label: d.name || `Service block ${d.name} at position ${d.order}`,
    })
  ).concat(
    {
      value: service.id,
      label: service.name,
    }
  )

  const _generateOptionsList = (blocks, defaultPrefix) => {

    const serviceBlock = data.find(d=>d.id===expressionObject.serviceBlockId);

    return blocks
      .filter((b) => {
        if (serviceBlock) {
          return b.service_id === serviceBlock?.metadata_?.chosen_service_id;
        } else {
          return isFromDifferentService && !editable ?
            b.service_id === block.service_id :
            b.service_id === service.id;
        }
      })
      .map((b) => ({
        label: b.name || `${defaultPrefix} ${b.id}`,
        value: b.id,
      }))
      .sort((a, b) => sortBlock(a, b, "label"));
  }

  const productsQuestionsOptions = _generateOptionsList(
    questions.filter(
      (q) =>
        q.input_type === "select" &&
        data.some(
          (d) => d.type === "option" && d.product_id && d.input_id === q.id
        )
    ),
    "Question",
  );
  const recursiveQuestionsOptions = _generateOptionsList(recursiveQuestions, "Recursive question");
  const numberInputQuestionsOptions = _generateOptionsList(
    questions.filter((q) =>
      q.input_type === "number_input"
      || (q.input_type === "select" &&
        data.some(
          (d) =>
            d.type === "option" &&
            d.option_value &&
            !isNaN(d.option_value) &&
            d.input_id === q.id
      ))
      || q.input_type === "connector_input"
    ),
    "Question"
  );
  const outputOptions = _generateOptionsList(outputs, "Output");

  // check if question options have value setted
  let productOptionValue = false;
  if (expressionObject?.outputType === "product_question") {
    const options = data.filter((b) => b.input_id === expressionObject.inputId);

    if (options.length && options.some((o) => o.option_value && o.product_id)) {
      productOptionValue = true;
    }
  }

  return (
    <div className="flex flex-col w-full mb-8 mt-8">
      <div className=" mb-2 flex text-center justify-center">
        <p className="font-bold">EXPRESSION GENERATOR</p>
      </div>

      <Select
        label="Select output type"
        disabled={!editable}
        options={[{ value: "", name: "" }, ...outputTypes]}
        value={expressionObject.outputType || ""}
        onChange={(e) => {
          updateExpression({
            original: Object.keys(expressionObject).length > 0
              ? expressionObject.original
              : expression,
            outputType: e.target.value,
            ...(expressionObject?.serviceBlockId ? {serviceBlockId: expressionObject.serviceBlockId} : {}),
            defaultValue: 0
          })
        }
        }
      />
      {
        servicesOptions.length > 1 && editable &&
        <SearchSelect
          label={"service"}
          options={servicesOptions}
          value={expressionObject?.serviceBlockId?
            servicesOptions.find(o=>o.value===expressionObject.serviceBlockId) :
            servicesOptions.find(o=>o.value===service.id)
          }
          onChange={(e) => {
            if (e.value === service.id) {
              // we want to remove serviceBlockId from expressionObject
              const {serviceBlockId, inputId, field, ...rest} = expressionObject;
              updateExpression(rest);
            } else {
              const {inputId, field, ...rest} = expressionObject;
              updateExpression({
                ...rest,
                serviceBlockId: e.value,
              });
            }
          }}
          isDisabled={!editable}
        />
      }

      {expressionObject?.outputType === "output" ? (
        <>
          <BlockSelector
            label="Select output"
            value={expressionObject.outputId}
            options={outputOptions}
            onChange={(selected) => updateExpression({
              outputType: "output",
              outputId: !!selected?.value ? parseInt(selected.value) : null,
              defaultValue: 0,
            })}
            showNotFoundWarning={lockMissingSelectedOptions}
            editable={editable}
          />
          <ServiceConfiguratorCompositeOutputPathSelector
            label="Select output field"
            selectedOutputId={expressionObject.outputId}
            outputs={outputs}
            onChange={(value) => {
              updateExpression({
                ...expressionObject,
                field: value,
              })
            }}
            value={expressionObject.field || ""}
          />
        </>
      ) : null}

      {expressionObject?.outputType === "product" ? (
        <SelectProduct
          location="ServiceConfiguratorCalculation"
          value={expressionObject.product || { value: "", label: "" }}
          onChange={(e) =>
            updateExpression({
              outputType: "product",
              product: e,
              defaultValue: 0,
              ...(expressionObject?.serviceBlockId ? {serviceBlockId: expressionObject.serviceBlockId} : {}),
            })
          }
          disabled={!editable}
        />
      ) : null}

      {expressionObject?.outputType === "product_question" ? (
        <BlockSelector
          label="Select question"
          value={expressionObject.inputId}
          options={productsQuestionsOptions}
          onChange={(selected) => {
            updateExpression({
              outputType: "product_question",
              inputId: !!selected?.value ? parseInt(selected.value) : null,
              required: true,
              defaultValue: 0,
              ...(expressionObject?.serviceBlockId ? {serviceBlockId: expressionObject.serviceBlockId} : {})
            })
          }}
          showNotFoundWarning={lockMissingSelectedOptions}
          editable={editable}
        />
      ) : null}
      {expressionObject?.outputType === "recursive_question" ? (
        <BlockSelector
          label="Select recursive question"
          value={expressionObject.inputId}
          options={recursiveQuestionsOptions}
          onChange={(selected) => {
            updateExpression({
              outputType: "recursive_question",
              inputId: !!selected?.value ? parseInt(selected.value) : null,
              required: false,
              defaultValue: 0,
              ...(expressionObject?.serviceBlockId ? {serviceBlockId: expressionObject.serviceBlockId} : {})
            })
          }}
          showNotFoundWarning={lockMissingSelectedOptions}
          editable={editable}
        />
      ) : null}
      {expressionObject?.outputType === "number_question" && <>
        <BlockSelector
          label="Select question"
          value={expressionObject.inputId}
          options={numberInputQuestionsOptions}
          onChange={(selected) => {
            updateExpression({
              outputType: "number_question",
              inputId: !!selected?.value ? parseInt(selected.value) : null,
              required: true,
              defaultValue: 0,
              ...(expressionObject?.serviceBlockId ? {serviceBlockId: expressionObject.serviceBlockId} : {})
            })
          }}
          showNotFoundWarning={lockMissingSelectedOptions}
          editable={editable}
        />
        <ServiceConfiguratorCompositeQuestionFieldSelector
          label="Question field"
          selectedQuestionId={expressionObject.inputId}
          questions={questions}
          onChange={(e) => {
            updateExpression({
              ...expressionObject,
              field: e.target.value,
            })
          }}
          value={expressionObject.field || ""}
          numericFieldsOnly={true}
          disabled={!editable}
        />
      </>}
      {(
        expressionObject?.outputType === "product_question"
        && expressionObject?.inputId
      ) || (
        expressionObject?.outputType === "product" &&
        expressionObject?.product
      ) ? (
        <Select
          label="Select column"
          options={[
            { value: "", name: "" },
            ...productColumns,
            ...(productOptionValue
              ? [{ value: "value", name: "Option value" }]
              : []),
          ]}
          value={expressionObject.column || ""}
          onChange={(e) =>
            updateExpression({
              ...expressionObject,
              column: e.target.value,
            })
          }
          disabled={!editable}
        />
      ) : null}
      {(
        expressionObject?.outputType === "product_question"
        || expressionObject?.outputType === "number_question"
      ) && expressionObject.inputId ? (
        <Select
          label="Required"
          options={[
            { value: true, name: "True" },
            { value: false, name: "False" },
          ]}
          value={expressionObject.required}
          onChange={(e) => {
            updateExpression({
              ...expressionObject,
              required: JSON.parse(e.target.value),
            });
          }}
          disabled={!editable}
        />
      ) : null}

      {expressionObject?.outputType === "number" ? (
        <Input
          value={expressionObject.value}
          type={"number"}
          label="Value"
          onChange={(e) => {
            updateExpression({ ...expressionObject, value: e.target.value });
          }}
          disabled={!editable}
        />
      ) : null}

      {(
        expressionObject?.outputType
        && expressionObject?.outputType !== "number"
        && expressionObject?.inputId
      ) ? (
        <Input
          value={expressionObject.defaultValue}
          type={"number"}
          label="Default value"
          onChange={(e) => {
            updateExpression({
              ...expressionObject,
              defaultValue: parseFloat(e.target.value),
            });
          }}
          disabled={!editable}
        />
      ) : null}

      {editable && <div className="mt-4 flex justify-end">
        <Button onClick={() => setSelectedOperator(null)}>DONE</Button>
      </div>}
    </div>
  );
};

export default ServiceConfiguratorBlockCalculationExpressionGenerator;
